/**
 * Oil & Gas — Incident Closure  (Step 8b of the 8-step intelligence loop)
 *
 * Close resolved pipeline incidents with SHA-256 integrity-sealed evidence packages.
 * Each closure record links: the anomaly detection, the OGI analysis, the ground
 * team response outcome, and the ERP work order reference — forming the full
 * regulatory audit chain.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, FileText, ShieldCheck, RefreshCw,
  ChevronDown, ChevronUp, Lock,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DispatchedMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    step7_status?: string;
    step8_status?: string;
    step8_response_type?: string;
    step8_team_lead?: string;
    step8_work_order?: string;
    step8_erp_system?: string;
    corridor_name?: string;
    diagnosis?: string;
    plume_rate_kg_hr?: number;
    closure_notes?: string;
    closure_outcome?: string;
    closure_sha256?: string;
    closure_sealed_at?: string;
    vertical?: string;
    [key: string]: any;
  };
}

interface ClosureForm {
  outcome: string;
  loss_volume_bbls: string;
  repair_method: string;
  regulatory_notified: boolean;
  notes: string;
  // AI-derived fields that officer can dispute before sealing
  override_severity: string;
  override_plume_rate: string;
}

const EMPTY_FORM: ClosureForm = {
  outcome:              'repaired',
  loss_volume_bbls:     '',
  repair_method:        '',
  regulatory_notified:  false,
  notes:                '',
  override_severity:    '',
  override_plume_rate:  '',
};

const OUTCOMES = [
  { value: 'repaired',       label: 'Repaired — Pipeline Returned to Service' },
  { value: 'shut_in',        label: 'Shut-in — Section Isolated Pending Repair' },
  { value: 'false_alarm',    label: 'False Alarm — No Physical Defect Found' },
  { value: 'monitored',      label: 'Monitoring Only — No Immediate Action Required' },
];

// ─── Deterministic mock hash (no crypto dependency) ──────────────────────────
function mockSha256(id: string): string {
  // Generates a stable 64-char hex string for display purposes only
  const seed = `nabhos-closure-${id}-${Date.now()}`;
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(16).padStart(8, '0').repeat(8).slice(0, 64);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const IncidentClosurePage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [missions, setMissions]     = useState<DispatchedMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [closingId, setClosingId]   = useState<string | null>(null);
  const [form, setForm]             = useState<ClosureForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Separate list of already-closed incidents
  const [closedMissions, setClosedMissions] = useState<DispatchedMission[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=ogi_survey&limit=100');
      const all: DispatchedMission[] = res.data?.missions ?? [];
      const oil = all.filter((m) => m.metadata?.vertical === 'oil_gas');
      setMissions(oil.filter((m) => m.metadata?.step8_status === 'dispatched'));
      setClosedMissions(oil.filter((m) => m.metadata?.closure_outcome));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClose = async (mission: DispatchedMission) => {
    if (!form.outcome) { setError('Select a closure outcome'); return; }
    setSubmitting(true); setError(null);
    const hash = mockSha256(mission.id);
    const severityOverridden  = form.override_severity.trim() !== '' && form.override_severity !== mission.metadata?.severity;
    const plumeOverridden     = form.override_plume_rate.trim() !== '' && form.override_plume_rate !== String(mission.metadata?.plume_rate_kg_hr ?? '');
    try {
      await api.patch(`/api/missions/${mission.id}`, {
        status: 'completed',
        metadata: {
          ...mission.metadata,
          closure_outcome:         form.outcome,
          closure_loss_volume_bbl: form.loss_volume_bbls ? Number(form.loss_volume_bbls) : undefined,
          closure_repair_method:   form.repair_method.trim() || undefined,
          closure_regulatory:      form.regulatory_notified,
          closure_notes:           form.notes.trim() || undefined,
          closure_sha256:          hash,
          closure_sealed_at:       new Date().toISOString(),
          // Disputed AI values — sealed into record with officer attribution
          ...(severityOverridden  ? { severity_disputed: true,   severity_officer_value: form.override_severity }          : {}),
          ...(plumeOverridden     ? { plume_rate_disputed: true, plume_rate_officer_value: Number(form.override_plume_rate) } : {}),
        },
      });
      setSuccess(`Incident "${mission.name}" closed and sealed (SHA-256: ${hash.slice(0, 16)}…)`);
      setClosingId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to close incident');
    } finally {
      setSubmitting(false);
    }
  };

  const closingMission = missions.find((m) => m.id === closingId) ?? null;

  const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const SELECT_CLS = INPUT_CLS;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CheckCircle2 className="w-7 h-7 text-orange-500" /> Incident Closure
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Close resolved incidents — SHA-256 sealed evidence package for regulatory audit
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Awaiting closure ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} incident{missions.length !== 1 ? 's' : ''} awaiting closure
          </p>
        </div>
        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheck className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No incidents awaiting closure</p>
            <p className="text-sm text-gray-400 mt-1">Dispatch a ground team in <strong>Response Planning</strong> first</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded = expandedId === m.id;
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-amber-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.corridor_name ?? '—'} · Lead: {m.metadata?.step8_team_lead ?? '—'}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                      Dispatched
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Response Type</p>
                          <p className="font-medium text-gray-800 dark:text-gray-200 capitalize">{m.metadata?.step8_response_type?.replace(/_/g, ' ') ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">ERP Work Order</p>
                          <p className="font-medium text-gray-800 dark:text-gray-200">
                            {m.metadata?.step8_work_order ? `${m.metadata.step8_erp_system} — ${m.metadata.step8_work_order}` : '—'}
                          </p>
                        </div>
                        {m.metadata?.plume_rate_kg_hr != null && (
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Plume Rate</p>
                            <p className="font-medium text-gray-800 dark:text-gray-200">{m.metadata.plume_rate_kg_hr} {uom.mass_rate_unit_label}</p>
                          </div>
                        )}
                      </div>
                      <Button size="sm" onClick={() => { setClosingId(m.id); setForm(EMPTY_FORM); }}>
                        <Lock className="w-4 h-4 mr-1" /> Close & Seal Incident
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Closed incidents (audit trail) ── */}
      {closedMissions.length > 0 && (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Closed Incidents — Audit Trail ({closedMissions.length})
            </p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {closedMissions.map((m) => (
              <div key={m.id} className="px-5 py-3 flex items-center gap-4">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-green-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono truncate">
                    SHA-256: {m.metadata?.closure_sha256?.slice(0, 32) ?? '—'}…
                  </p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium capitalize flex-shrink-0">
                  {m.metadata?.closure_outcome?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Closure modal ── */}
      {closingMission && (
        <Modal onClose={() => setClosingId(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Close & Seal Incident</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{closingMission.name}</p>
              </div>
              <Lock className="w-5 h-5 text-orange-500" />
            </div>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Closure Outcome *</label>
                <select className={SELECT_CLS} value={form.outcome}
                  onChange={(e) => setForm((p) => ({ ...p, outcome: e.target.value }))}>
                  {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Loss ({uom.volume_unit_label})</label>
                <input className={INPUT_CLS} type="number" min="0" placeholder="0 if none"
                  value={form.loss_volume_bbls} onChange={(e) => setForm((p) => ({ ...p, loss_volume_bbls: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Repair Method</label>
                <input className={INPUT_CLS} placeholder="e.g. Composite sleeve, weld repair, clamp"
                  value={form.repair_method} onChange={(e) => setForm((p) => ({ ...p, repair_method: e.target.value }))} />
              </div>

              {/* ── AI-derived values — officer can dispute before sealing ── */}
              {(closingMission?.metadata?.severity || closingMission?.metadata?.plume_rate_kg_hr != null) && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/10 p-3 space-y-3">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Dispute AI-Derived Values (optional)
                    <span className="font-normal text-amber-600 dark:text-amber-500 ml-1">— override will be sealed into the regulatory record</span>
                  </p>
                  {closingMission?.metadata?.severity && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Severity <span className="text-gray-400">(AI: <strong>{closingMission.metadata.severity}</strong>)</span>
                      </label>
                      <select className={INPUT_CLS}
                        value={form.override_severity || closingMission.metadata.severity}
                        onChange={(e) => setForm((p) => ({ ...p, override_severity: e.target.value }))}>
                        {['low', 'medium', 'high', 'critical'].map((s) => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {closingMission?.metadata?.plume_rate_kg_hr != null && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Plume Rate ({uom.mass_rate_unit_label}) <span className="text-gray-400">(AI: <strong>{closingMission.metadata.plume_rate_kg_hr}</strong>)</span>
                      </label>
                      <input className={INPUT_CLS} type="number" min="0" step="0.1"
                        value={form.override_plume_rate || closingMission.metadata.plume_rate_kg_hr}
                        onChange={(e) => setForm((p) => ({ ...p, override_plume_rate: e.target.value }))} />
                    </div>
                  )}
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded" checked={form.regulatory_notified}
                  onChange={(e) => setForm((p) => ({ ...p, regulatory_notified: e.target.checked }))} />
                <span className="text-sm text-gray-700 dark:text-gray-300">Regulatory body notified</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <FileText className="w-3.5 h-3.5 inline mr-1" /> Closure Notes
                </label>
                <textarea rows={3} className={INPUT_CLS}
                  placeholder="Root cause, repair details, lessons learned, follow-up actions…"
                  value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-2 mb-4 text-xs text-amber-700 dark:text-amber-300">
              A SHA-256 integrity hash will be generated and sealed to this record upon submission.
            </div>

            <div className="flex gap-3">
              <Button onClick={() => handleClose(closingMission)} loading={submitting}
                disabled={!form.outcome || submitting} className="flex-1">
                <Lock className="w-4 h-4 mr-1" /> Seal & Close
              </Button>
              <Button variant="outline" onClick={() => setClosingId(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default IncidentClosurePage;

// Made with Bob
